import { memo, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faMicrophone,
  faSpinner,
  faAlignLeft,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";

const PART_OPTIONS = [
  { id: "text", label: "Text", icon: faAlignLeft },
  { id: "image", label: "Image", icon: faImage },
  { id: "voice", label: "Voice", icon: faMicrophone },
];

function SendNotificationForm({
  onClose,
  students = [],
  onReset,
  tableFor = "",
}) {
  const [parts, setParts] = useState({
    text: true,
    image: false,
    voice: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const studentIds = useMemo(() => {
    if (!students?.length) return [];

    const extractId = (student) => {
      if (typeof student === "number" || typeof student === "string") {
        return student;
      }
      if (!student || typeof student !== "object") return null;

      // Attendance / nested tables often use student_id
      if (tableFor === "sessionAttendance" || student.student_id) {
        return student.student_id ?? student.key ?? student.id;
      }

      return (
        student.key ??
        student.id ??
        student.student_id ??
        student.student?.id ??
        student.student?.key ??
        null
      );
    };

    return [
      ...new Set(
        students
          .map(extractId)
          .filter((id) => id !== null && id !== undefined && id !== ""),
      ),
    ];
  }, [students, tableFor]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      body: "",
    },
  });

  const [sendState, setSendState] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  const [sendSuccess, sendErrors, sendLoading] = useAxios(
    process.env.REACT_APP_ADMIN_BULK_NOTIFICATION_API,
    "POST",
    sendState.flag,
    sendState.dependency,
    sendState.data,
    true
  );

  useEffect(() => {
    if (!sendSuccess) return;
    toast.success(
      sendSuccess?.message ||
        `Notification sent to ${sendSuccess?.data?.sent ?? studentIds.length} students`
    );
    reset();
    setImageFile(null);
    setVoiceFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setParts({ text: true, image: false, voice: false });
    onClose?.();
    onReset?.();
  }, [sendSuccess]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const togglePart = (id) => {
    setParts((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (!next.text && !next.image && !next.voice) {
        toast.error("اختَر نوع واحد على الأقل");
        return prev;
      }
      if (id === "image" && prev.image) {
        setImageFile(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      if (id === "voice" && prev.voice) {
        setVoiceFile(null);
      }
      return next;
    });
  };

  const onPickImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const onPickVoice = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setVoiceFile(file);
  };

  const onSubmit = (data) => {
    if (!studentIds.length) {
      toast.error("اختَر طالب واحد على الأقل");
      return;
    }

    if (parts.text && !String(data.body || "").trim()) {
      toast.error("اكتب نص الإشعار");
      return;
    }

    if (parts.image && !imageFile) {
      toast.error("اختَر صورة");
      return;
    }

    if (parts.voice && !voiceFile) {
      toast.error("اختَر ملف صوت");
      return;
    }

    if (!parts.text && !parts.image && !parts.voice) {
      toast.error("اختَر نوع واحد على الأقل");
      return;
    }

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("body", parts.text ? data.body || "" : data.body || "");
    studentIds.forEach((id) => formData.append("student_ids[]", id));
    if (parts.image && imageFile) {
      formData.append("image", imageFile);
    }
    if (parts.voice && voiceFile) {
      formData.append("voice", voiceFile);
    }

    setSendState({
      flag: "bulkSendNotification",
      dependency: !sendState.dependency,
      data: formData,
    });
  };

  const selectedPartsLabel = PART_OPTIONS.filter((p) => parts[p.id])
    .map((p) => p.label)
    .join(" + ");

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="admin-send-notification-form flex w-full flex-col gap-5 py-2 text-slate-800 dark:text-slate-200"
    >
      <p className="text-base text-slate-500 dark:text-slate-400">
        Selected students:{" "}
        <strong className="text-slate-800 dark:text-slate-100">
          {studentIds.length}
        </strong>
      </p>

      <div>
        <p className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Content types (يمكن اختيار أكتر من نوع)
        </p>
        <div className="flex flex-wrap gap-2">
          {PART_OPTIONS.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => togglePart(type.id)}
              className={`inline-flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm font-semibold transition-all ${
                parts[type.id]
                  ? "border-secondary bg-secondary text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-secondary hover:text-secondary dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:border-blue-400 dark:hover:text-blue-300"
              }`}
            >
              <FontAwesomeIcon icon={type.icon} />
              {type.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Sending:{" "}
          <strong className="text-slate-700 dark:text-slate-200">
            {selectedPartsLabel || "—"}
          </strong>
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="notif_title"
          className="font-semibold text-slate-800 dark:text-slate-100"
        >
          Title
        </label>
        <input
          id="notif_title"
          className="signin-inputs signin-input w-full pl-4"
          placeholder="عنوان الإشعار"
          {...register("title", { required: true, maxLength: 255 })}
        />
        {errors.title && (
          <p className="text-sm text-red-500">العنوان مطلوب</p>
        )}
      </div>

      {(parts.text || parts.image || parts.voice) && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor="notif_body"
            className="font-semibold text-slate-800 dark:text-slate-100"
          >
            {parts.text ? "Message" : "Caption (optional)"}
          </label>
          <textarea
            id="notif_body"
            rows={8}
            className="signin-inputs signin-input w-full !h-40 min-h-[10rem] resize-y pl-4 pt-3"
            placeholder={
              parts.text
                ? "اكتب نص الإشعار..."
                : "وصف اختياري مع الصورة/الصوت"
            }
            {...register("body", {
              required: parts.text,
              maxLength: 5000,
            })}
          />
          {errors.body && parts.text && (
            <p className="text-sm text-red-500">النص مطلوب</p>
          )}
        </div>
      )}

      {parts.image && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor="notif_image"
            className="font-semibold text-slate-800 dark:text-slate-100"
          >
            Image file
          </label>
          <input
            id="notif_image"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={onPickImage}
            className="w-full text-sm text-slate-700 dark:text-slate-300"
          />
          {imageFile && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {imageFile.name}
            </p>
          )}
          {imagePreview && (
            <img
              src={imagePreview}
              alt="preview"
              className="mt-1 max-h-48 rounded-xl object-contain"
            />
          )}
        </div>
      )}

      {parts.voice && (
        <div className="flex flex-col gap-2">
          <label
            htmlFor="notif_voice"
            className="font-semibold text-slate-800 dark:text-slate-100"
          >
            Voice file
          </label>
          <input
            id="notif_voice"
            type="file"
            accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg,.webm,.3gp"
            onChange={onPickVoice}
            className="w-full text-sm text-slate-700 dark:text-slate-300"
          />
          {voiceFile && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {voiceFile.name}
            </p>
          )}
        </div>
      )}

      {sendErrors?.response?.data?.errors && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950/50 dark:text-red-300 dark:ring-1 dark:ring-red-800/60">
          {Object.values(sendErrors.response.data.errors)
            .flat()
            .map((msg, i) => (
              <p key={i}>{msg}</p>
            ))}
        </div>
      )}

      <button
        type="submit"
        disabled={sendLoading && sendState.flag}
        className="submit mt-2 w-full"
      >
        {sendLoading && sendState.flag ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : (
          `Send to ${studentIds.length} student${
            studentIds.length === 1 ? "" : "s"
          }`
        )}
      </button>
    </form>
  );
}

export default memo(SendNotificationForm);
